import { Fragment } from "react";
import { relativeTime } from "../lib/format";
import type { Run } from "../lib/types";

/**
 * The poll log. Operator-facing, so it is dense on purpose: one row per run,
 * every counter the pipeline reports, and the 304 ratio rendered as a bar —
 * that ratio is the single number that says whether the conditional-request
 * cache is doing its job.
 */

const STYLE_ID = "aw-run-table-styles";

const CSS = `
.aw-rt {
  --ink: #0D0D0D;
  --red: #FF0000;
  --pink: #E89AC7;
  --tag: #FFFFFF;
  --rule: rgba(13, 13, 13, 0.14);
  --mute: #6B6B6B;
  --anton: "Anton", "Oswald", Impact, Haettenschweiler, sans-serif;
  --oswald: "Oswald", "Anton", "Arial Narrow", Arial, sans-serif;
  --sora: "Sora", "Helvetica Neue", Helvetica, Arial, sans-serif;
}

/* The page must never scroll sideways; the table scrolls inside its own box. */
.aw-rt-scroll {
  overflow-x: auto;
  background: var(--tag);
  border: 1px solid var(--ink);
  -webkit-overflow-scrolling: touch;
}

.aw-rt-table {
  width: 100%;
  min-width: 860px;
  border-collapse: collapse;
  font-family: var(--sora);
  color: var(--ink);
}

.aw-rt-table thead th {
  font-family: var(--sora);
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--mute);
  text-align: right;
  padding: 12px 14px 10px;
  border-bottom: 1px solid var(--ink);
  white-space: nowrap;
}
.aw-rt-table thead th.aw-rt-l { text-align: left; }

.aw-rt-table tbody td {
  padding: 12px 14px;
  border-bottom: 1px solid var(--rule);
  text-align: right;
  vertical-align: top;
  white-space: nowrap;
}
.aw-rt-table tbody tr:last-child td { border-bottom: 0; }
.aw-rt-table tbody tr.aw-rt-live td { background: rgba(232, 154, 199, 0.14); }
.aw-rt-table tbody tr.aw-rt-err td { border-bottom: 0; }

/* These four beat the right-aligned numeric default, so they carry its specificity. */
.aw-rt-table tbody td.aw-rt-mode {
  font-family: var(--anton);
  font-size: 15px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: left;
}

.aw-rt-table tbody td.aw-rt-when {
  text-align: left;
  font-size: 12px;
  line-height: 1.25;
}
.aw-rt-when .aw-rt-sub { display: block; }

.aw-rt-num {
  font-family: var(--oswald);
  font-size: 15px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  line-height: 1.15;
}
.aw-rt-num.is-zero { color: var(--mute); }
/* A poll that logged changes is the one worth looking at. */
.aw-rt-num.is-events.has-events { color: var(--red); }

.aw-rt-sub {
  display: block;
  margin-top: 3px;
  font-family: var(--sora);
  font-size: 9px;
  font-weight: 400;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--mute);
}

.aw-rt-table tbody td.aw-rt-status {
  text-align: left;
}
.aw-rt-chip {
  display: inline-block;
  font-family: var(--oswald);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 3px 8px 2px;
  border: 1px solid var(--ink);
}
.aw-rt-chip.is-completed { background: var(--ink); color: var(--tag); }
.aw-rt-chip.is-running { background: var(--pink); color: var(--ink); border-color: var(--ink); }
.aw-rt-chip.is-failed { background: var(--red); color: var(--tag); border-color: var(--red); }
.aw-rt-chip.is-cancelled { background: transparent; color: var(--mute); border-color: var(--mute); }

/* 304 ratio: number plus a bar, because the trend matters more than the digits. */
.aw-rt-meter {
  display: block;
  width: 64px;
  height: 3px;
  margin: 6px 0 0 auto;
  background: rgba(13, 13, 13, 0.12);
}
.aw-rt-meter i {
  display: block;
  height: 100%;
  background: var(--pink);
}
.aw-rt-meter.is-empty i { background: transparent; }

.aw-rt-table tbody td.aw-rt-errmsg {
  text-align: left;
  padding: 0 14px 14px;
  border-bottom: 1px solid var(--rule);
  white-space: normal;
}
.aw-rt-errmsg span {
  display: block;
  border-left: 3px solid var(--red);
  padding: 6px 0 6px 10px;
  font-family: var(--sora);
  font-size: 11px;
  line-height: 1.45;
  color: var(--red);
  white-space: normal;
  overflow-wrap: anywhere;
}

.aw-rt-empty {
  margin: 0;
  background: var(--tag);
  border: 1px solid var(--ink);
  padding: 28px 20px;
  text-align: center;
  font-family: var(--sora);
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--mute);
}

@media (max-width: 640px) {
  .aw-rt-table thead th,
  .aw-rt-table tbody td { padding: 10px; }
}
`;

if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

const clockTime = (iso: string): string => {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return "";
  return new Date(then).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDuration = (ms?: number): string => {
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
};

const count = (n?: number): string =>
  typeof n === "number" && Number.isFinite(n) ? n.toLocaleString() : "0";

const STATUS_LABEL: Record<Run["status"], string> = {
  running: "running",
  completed: "completed",
  failed: "failed",
  cancelled: "cancelled",
};

export interface RunTableProps {
  runs: Run[];
}

export function RunTable({ runs }: RunTableProps) {
  if (runs.length === 0) {
    return (
      <div className="aw-rt">
        <p className="aw-rt-empty">No polls recorded yet</p>
      </div>
    );
  }

  return (
    <div className="aw-rt">
      <div className="aw-rt-scroll">
        <table className="aw-rt-table" aria-label="Poll history">
          <thead>
            <tr>
              <th className="aw-rt-l" scope="col">
                Mode
              </th>
              <th className="aw-rt-l" scope="col">
                Status
              </th>
              <th className="aw-rt-l" scope="col">
                Started
              </th>
              <th scope="col">Duration</th>
              <th scope="col">Pages</th>
              <th scope="col">Products</th>
              <th scope="col">Events</th>
              <th scope="col">Requests</th>
              <th scope="col">304 cache</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => {
              const requests = run.requestsMade ?? 0;
              const notModified = run.notModifiedCount ?? 0;
              const ratio = requests > 0 ? Math.min(1, notModified / requests) : 0;
              const isRunning = run.status === "running";
              // A run still in flight has no durationMs yet, so measure from the clock.
              const elapsed = isRunning ? Date.now() - Date.parse(run.startedAt) : run.durationMs;
              const events = run.eventsCreated ?? 0;

              return (
                <Fragment key={run._id}>
                  <tr className={isRunning ? "aw-rt-live" : undefined}>
                    <td className="aw-rt-mode">{run.mode}</td>
                    <td className="aw-rt-status">
                      <span className={`aw-rt-chip is-${run.status}`}>
                        {STATUS_LABEL[run.status]}
                      </span>
                    </td>
                    <td className="aw-rt-when">
                      {relativeTime(run.startedAt)}
                      <span className="aw-rt-sub">{clockTime(run.startedAt)}</span>
                    </td>
                    <td className="aw-rt-num">{formatDuration(elapsed)}</td>
                    <td className={`aw-rt-num${run.pagesCrawled ? "" : " is-zero"}`}>
                      {count(run.pagesCrawled)}
                    </td>
                    <td className={`aw-rt-num${run.productsSeen ? "" : " is-zero"}`}>
                      {count(run.productsSeen)}
                      <span className="aw-rt-sub">{count(run.variantsSeen)} variants</span>
                    </td>
                    <td className={`aw-rt-num is-events${events ? " has-events" : " is-zero"}`}>
                      {count(events)}
                    </td>
                    <td className={`aw-rt-num${requests ? "" : " is-zero"}`}>{count(requests)}</td>
                    <td className={`aw-rt-num${requests ? "" : " is-zero"}`}>
                      {requests > 0 ? `${Math.round(ratio * 100)}%` : "—"}
                      <span className="aw-rt-sub">{count(notModified)} not modified</span>
                      <span className={`aw-rt-meter${requests > 0 ? "" : " is-empty"}`}>
                        <i style={{ width: `${Math.round(ratio * 100)}%` }} />
                      </span>
                    </td>
                  </tr>
                  {run.error ? (
                    <tr className="aw-rt-err">
                      <td className="aw-rt-errmsg" colSpan={9}>
                        <span>{run.error}</span>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default RunTable;
